import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    
    // Restore cart count from cache first
    this.#restoreCartCountFromCache();
    
    // Then verify against Shopify Cart API after page load to allow cart refresh to complete
    if (document.readyState === 'complete') {
      setTimeout(() => this.#fetchCartCountFromAPI(), 600);
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => this.#fetchCartCountFromAPI(), 600);
      }, { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  onCartUpdate = async (/** @type {CartUpdateEvent} */ event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    this.renderCartBubble(itemCount);
  };

  /**
   * Restores cart count from cache (sessionStorage)
   */
  #restoreCartCountFromCache() {
    try {
      const cached = sessionStorage.getItem('cart-count');
      if (cached) {
        const { value, timestamp } = JSON.parse(cached);
        // Use cached value if it's less than 5 minutes old
        if (timestamp && Date.now() - timestamp < 300000) {
          const count = parseInt(value, 10);
          if (!isNaN(count) && count >= 0) {
            this.renderCartBubble(count, false);
          }
        }
      }
    } catch (_) {
    }
  }

  /**
   * Fetches cart count directly from Shopify Cart API
   * This is more reliable than parsing HTML
   */
  #fetchCartCountFromAPI = async () => {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      
      if (!response.ok) return;
      
      const cart = await response.json();
      const itemCount = cart.item_count || 0;
      
      // Update cache and icon if it differs
      if (itemCount !== this.currentCartCount) {
        this.renderCartBubble(itemCount, false);
      }
    } catch (_) {
      // Fallback to DOM check if API fails
      this.#syncCartCountFromDOM();
    }
  };

  /**
   * Syncs cart count by reading from the actual DOM (fallback)
   */
  #syncCartCountFromDOM = () => {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (!cartItemsComponent) return;
    
    const cartItemCountElement = cartItemsComponent.querySelector('[ref="cartItemCount"]');
    if (cartItemCountElement?.textContent) {
      const actualCount = parseInt(cartItemCountElement.textContent, 10);
      if (!isNaN(actualCount)) {
        // Update cache and icon if it differs
        if (actualCount !== this.currentCartCount) {
          this.renderCartBubble(actualCount, false);
        }
      }
    }
  };

  renderCartBubble = async (/** @type {number} */ itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    // Cache the cart count so it persists across page navigation
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
